<template>
  <div v-bind="$attrs" :class="getListClass">
    <div
      v-for="item in options"
      :key="item.key || item.field || item.label"
      :class="['base-list-row', getItemClass(item, 'rowClass')]"
    >
      <div
        :class="['list-row-label', getItemClass(item, 'labelClass'), { truncate }]"
        :data-lang-key="item.langKey"
      >
        {{ item.label }}
      </div>
      <div :class="['list-row-value', getItemClass(item, 'valueClass'), { truncate }]">
        <component
          :is="() => item.customRender!({ text: item.value, record, column: item }) ?? empty"
          v-if="item.customRender"
        />
        <span v-else>
          {{
            isArray(item.value)
              ? item.value.join('、') || empty
              : (item.value ?? (item.field ? record[item.field] : empty) ?? empty)
          }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, VNode } from 'vue';
  import type { Option } from '@mts-fe/types';
  import { isArray } from '@mts-fe/utils';
  import { useDesign } from '@mts/hooks/web/useDesign';
  import { type ColumnCellProps, getGlobalParams } from '@mts/render';

  defineOptions({ name: 'DataList' });

  export type DataListClass = string | ((opt: ColumnCellProps) => string);

  export type DataListItem = Partial<Option> & {
    key?: string;
    field?: string;
    langKey?: string;
    rowClass?: DataListClass;
    labelClass?: DataListClass;
    valueClass?: DataListClass;
    customRender?: (opt: ColumnCellProps) => VNode | VNode[] | string;
  };
  export interface DataListProps {
    /** 数据源 */
    options?: DataListItem[];
    /** 对象映射集 */
    record?: Recordable;
    horizontal?: boolean;
    /** 超出截断，默认开启 */
    truncate?: boolean;
    /** 紧凑模式 */
    compact?: boolean;
    rowClass?: DataListClass;
    labelClass?: DataListClass;
    valueClass?: DataListClass;
    /** 空值展示形式 */
    empty?: '-' | '';
  }

  const props = withDefaults(defineProps<DataListProps>(), {
    record: () => ({}),
    truncate: true,
    empty: '-',
  });

  const { prefixCls } = useDesign('base-list');
  const getListClass = computed(() => {
    const classes: (string | Record<string, boolean>)[] = [
      prefixCls,
      props.compact ? 'gap-4px' : 'gap-8px',
    ];

    if (props.horizontal) {
      classes.push(`${prefixCls}--horizontal`);
    }

    return classes;
  });

  const globalParams = getGlobalParams();
  const getItemClass = (item: DataListItem, classKey: string) => {
    const classNames: string[] = [];
    const opt = { text: item.value, column: item, record: props.record, ...globalParams };

    const defClass = props[classKey];
    defClass && classNames.push(typeof defClass === 'function' ? defClass(opt) : defClass);

    const itemClass = item[classKey];
    itemClass && classNames.push(typeof itemClass === 'function' ? itemClass(opt) : itemClass);
    return classNames.join(' ');
  };
</script>

<style lang="less">
  @prefix-cls: ~'@{namespace}-base-list';

  .@{prefix-cls} {
    display: grid;

    .base-list-row {
      display: grid;
    }

    .list-row-label {
      color: #8991a1;
    }

    .list-row-value {
      flex: 1;
    }

    &--horizontal {
      .base-list-row {
        grid-auto-flow: column;
        grid-template-columns: auto 1fr;
      }

      .list-row-label {
        margin-right: 16px;
      }

      .list-row-value {
        min-width: 50%;
        text-align: right;
      }
    }
  }
</style>
